import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Building2, Loader2, Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '../components/Button';
import ConfirmationDialog from '../components/ConfirmationDialog';
import SearchField from '../components/SearchField';
import {
  StandardTable,
  StandardTableBody,
  StandardTableCell,
  StandardTableHeader,
  StandardTableHeaderCell,
  StandardTableRow,
} from '../components/StandardTable';
import { usePageTitle } from '../hooks/usePageTitle';
import api from '../lib/api';
import FormModal from '@shared/ui/FormModal';
import {
  AudienceGroup,
  AudienceGroupOrganization,
  ClientOrganizationSearchResult,
  normalizeAudienceGroup,
  normalizeAudienceGroups,
  normalizeClientOrganizationSearchResults,
} from '../utils/announcementNormalizers';

const AdminAudienceGroupsPage: React.FC = () => {
  usePageTitle('Audience Groups', 'Manage reusable client groups for system announcements');

  const [groups, setGroups] = useState<AudienceGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<AudienceGroup | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<AudienceGroup | null>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [memberRoleDefault, setMemberRoleDefault] = useState<'all' | 'org_admin'>('all');
  const [selectedOrganizations, setSelectedOrganizations] = useState<AudienceGroupOrganization[]>([]);
  const [orgSearch, setOrgSearch] = useState('');
  const [orgResults, setOrgResults] = useState<ClientOrganizationSearchResult[]>([]);
  const [isSearchingOrgs, setIsSearchingOrgs] = useState(false);

  const loadGroups = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/admin/audience-groups');
      setGroups(normalizeAudienceGroups(response.data.data.groups || []));
    } catch {
      toast.error('Failed to load audience groups');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  useEffect(() => {
    if (!editorOpen) return undefined;

    const timer = window.setTimeout(async () => {
      setIsSearchingOrgs(true);
      try {
        const params = new URLSearchParams({
          limit: '20',
          ...(orgSearch.trim() && { q: orgSearch.trim() }),
        });
        const response = await api.get(`/admin/audience-groups/client-organizations/search?${params.toString()}`);
        setOrgResults(normalizeClientOrganizationSearchResults(response.data.data?.organizations || []));
      } catch {
        setOrgResults([]);
        toast.error('Failed to search client organizations');
      } finally {
        setIsSearchingOrgs(false);
      }
    }, 300);

    return () => window.clearTimeout(timer);
  }, [editorOpen, orgSearch]);

  const openCreateEditor = () => {
    setEditingGroup(null);
    setName('');
    setDescription('');
    setMemberRoleDefault('all');
    setSelectedOrganizations([]);
    setOrgSearch('');
    setEditorOpen(true);
  };

  const openEditEditor = async (group: AudienceGroup) => {
    try {
      const response = await api.get(`/admin/audience-groups/${group.id}`);
      const fullGroup = normalizeAudienceGroup(response.data.data.group);
      setEditingGroup(fullGroup);
      setName(fullGroup.name);
      setDescription(fullGroup.description || '');
      setMemberRoleDefault(fullGroup.memberRoleDefault);
      setSelectedOrganizations(fullGroup.organizations || []);
      setOrgSearch('');
      setEditorOpen(true);
    } catch {
      toast.error('Failed to load audience group');
    }
  };

  const selectedOrgIds = useMemo(
    () => new Set(selectedOrganizations.map((org) => org.id)),
    [selectedOrganizations],
  );

  const addOrganization = (org: ClientOrganizationSearchResult) => {
    if (selectedOrgIds.has(org.id)) return;
    setSelectedOrganizations((current) => [
      ...current,
      {
        id: org.id,
        name: org.name,
        orgCategory: org.orgCategory,
        subscription: org.subscription,
      },
    ]);
  };

  const removeOrganization = (orgId: string) => {
    setSelectedOrganizations((current) => current.filter((org) => org.id !== orgId));
  };

  const saveGroup = async () => {
    if (!name.trim()) {
      toast.error('Group name is required');
      return;
    }

    setIsSaving(true);
    try {
      let groupId = editingGroup?.id;

      if (editingGroup) {
        await api.put(`/admin/audience-groups/${editingGroup.id}`, {
          name: name.trim(),
          description: description.trim() || null,
          member_role_default: memberRoleDefault,
        });
      } else {
        const response = await api.post('/admin/audience-groups', {
          name: name.trim(),
          description: description.trim() || null,
          member_role_default: memberRoleDefault,
        });
        groupId = response.data.data.group.id;
      }

      await api.put(`/admin/audience-groups/${groupId}/organizations`, {
        organization_ids: selectedOrganizations.map((org) => org.id),
      });

      toast.success(editingGroup ? 'Audience group updated' : 'Audience group created');
      setEditorOpen(false);
      await loadGroups();
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(message || 'Failed to save audience group');
    } finally {
      setIsSaving(false);
    }
  };

  const confirmArchive = async () => {
    if (!archiveTarget) return;

    try {
      await api.delete(`/admin/audience-groups/${archiveTarget.id}`);
      toast.success('Audience group archived');
      setArchiveTarget(null);
      await loadGroups();
    } catch {
      toast.error('Failed to archive audience group');
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-text-primary dark:text-text-dark-primary">
            Audience Groups
          </h1>
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary mt-1">
            Save reusable organization lists for targeted client announcements.
          </p>
        </div>
        <Button variant="primary" icon={Plus} onClick={openCreateEditor}>
          New Group
        </Button>
      </div>

      <div className="card">
        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-text-secondary dark:text-text-dark-secondary">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Loading groups...
          </div>
        ) : groups.length === 0 ? (
          <div className="py-12 text-center text-text-secondary dark:text-text-dark-secondary">
            No audience groups yet. Create one to reuse client targeting.
          </div>
        ) : (
          <StandardTable horizontalScroll>
            <StandardTableHeader>
              <StandardTableRow>
                <StandardTableHeaderCell>Name</StandardTableHeaderCell>
                <StandardTableHeaderCell>Organizations</StandardTableHeaderCell>
                <StandardTableHeaderCell>Default Role</StandardTableHeaderCell>
                <StandardTableHeaderCell>Updated</StandardTableHeaderCell>
                <StandardTableHeaderCell>Actions</StandardTableHeaderCell>
              </StandardTableRow>
            </StandardTableHeader>
            <StandardTableBody>
              {groups.map((group) => (
                <StandardTableRow key={group.id}>
                  <StandardTableCell>
                    <div className="font-medium text-text-primary dark:text-text-dark-primary">
                      {group.name}
                    </div>
                    {group.description && (
                      <div className="text-xs text-text-secondary dark:text-text-dark-secondary mt-1">
                        {group.description}
                      </div>
                    )}
                  </StandardTableCell>
                  <StandardTableCell>{group.organizationCount ?? 0}</StandardTableCell>
                  <StandardTableCell>
                    {group.memberRoleDefault === 'org_admin' ? 'Org admins' : 'All members'}
                  </StandardTableCell>
                  <StandardTableCell>
                    {group.updatedAt ? new Date(group.updatedAt).toLocaleDateString() : '—'}
                  </StandardTableCell>
                  <StandardTableCell>
                    <div className="flex items-center gap-2">
                      <Button variant="secondary" size="sm" onClick={() => openEditEditor(group)}>
                        Edit
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        icon={Trash2}
                        onClick={() => setArchiveTarget(group)}
                      >
                        Archive
                      </Button>
                    </div>
                  </StandardTableCell>
                </StandardTableRow>
              ))}
            </StandardTableBody>
          </StandardTable>
        )}
      </div>

      {editorOpen && (
        <FormModal
          isOpen={true}
          title={editingGroup ? 'Edit audience group' : 'Create audience group'}
          description="Choose organizations once, then reuse this group when sending announcements."
          onClose={() => setEditorOpen(false)}
          onSubmit={saveGroup}
          size="xl"
          submitLabel={editingGroup ? 'Save changes' : 'Create group'}
          isSubmitting={isSaving}
          isDirty={name.trim().length > 0}
        >
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
                    Name *
                  </label>
                  <input
                    className="input w-full"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    placeholder="VIP clients"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
                    Default member role
                  </label>
                  <select
                    className="input w-full"
                    value={memberRoleDefault}
                    onChange={(event) => setMemberRoleDefault(event.target.value as 'all' | 'org_admin')}
                  >
                    <option value="all">All organization members</option>
                    <option value="org_admin">Org admins only</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
                  Description
                </label>
                <textarea
                  className="input w-full min-h-[80px]"
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  placeholder="Optional notes about who belongs in this group"
                />
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-primary-700 dark:text-primary-400" />
                  <h3 className="text-sm font-semibold text-text-primary dark:text-text-dark-primary">
                    Organizations
                  </h3>
                </div>

                {selectedOrganizations.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {selectedOrganizations.map((org) => (
                      <span
                        key={org.id}
                        className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-border-light dark:border-border-dark bg-background-tertiary dark:bg-background-dark-tertiary text-sm text-text-primary dark:text-text-dark-primary"
                      >
                        {org.name} ({org.subscription})
                        <button
                          type="button"
                          className="text-text-secondary dark:text-text-dark-secondary hover:text-text-primary dark:hover:text-text-dark-primary"
                          onClick={() => removeOrganization(org.id)}
                          aria-label={`Remove ${org.name}`}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                    No organizations selected yet.
                  </p>
                )}

                <SearchField
                  value={orgSearch}
                  onChange={setOrgSearch}
                  placeholder="Search client organizations (paid or trial)..."
                />

                <div className="border border-border-light dark:border-border-dark rounded-lg max-h-48 overflow-y-auto">
                  {isSearchingOrgs ? (
                    <div className="p-4 text-sm text-text-secondary dark:text-text-dark-secondary flex items-center">
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Searching...
                    </div>
                  ) : orgResults.length === 0 ? (
                    <div className="p-4 text-sm text-text-secondary dark:text-text-dark-secondary">
                      No organizations found.
                    </div>
                  ) : (
                    orgResults.map((org) => (
                      <button
                        key={org.id}
                        type="button"
                        className="w-full text-left px-4 py-3 border-b border-border-light dark:border-border-dark last:border-b-0 hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary disabled:opacity-50"
                        disabled={selectedOrgIds.has(org.id)}
                        onClick={() => addOrganization(org)}
                      >
                        <div className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                          {org.name}
                        </div>
                        <div className="text-xs text-text-secondary dark:text-text-dark-secondary">
                          {org.orgCategory} · {org.subscription}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>

            </div>
        </FormModal>
      )}

      <ConfirmationDialog
        isOpen={!!archiveTarget}
        title="Archive audience group?"
        message={`"${archiveTarget?.name || 'This group'}" will be hidden from future announcements.`}
        confirmText="Archive"
        variant="warning"
        onConfirm={confirmArchive}
        onCancel={() => setArchiveTarget(null)}
      />
    </div>
  );
};

export default AdminAudienceGroupsPage;
